import { ExpressionType } from '../expressions/expression';
import { Named, Owned } from './named';
import { Observable } from 'rxjs';
import { IResourceBase } from '../../shared/utils/resourcefiles.utils';

export type ModelValidator = (
    owner?: {},
    context?: IValidationContext
) => IViolation[] | Promise<IViolation[]> | undefined;
export type AnyConstraint = IConstraint | IStringConstraint | INumberConstraint;

export type ResourceContextType =
    | 'root'
    | 'adf'
    | 'a365'
    | 'tridentWorkspace'
    | 'mountedDataFactory'
    | 'dedicatedAdfInIframe'
    | 'cmdApiApp';

export type ValidationErrorType =
    | 'Required'
    | 'Readonly'
    | 'Pattern' // keywords & regex
    | 'Range'
    | 'InvalidType'
    | 'DuplicatedName'
    | 'MaxSize'
    | 'Other';

export interface IResourceContext {
    /**
     * ID of a given resource context.
     * When contextType is 'tridentWorkspace', value is the workspace object id.
     * When contextType is 'mountedDataFactory', value is the object id of mounted data factory artifact.
     */
    contextId: string;
    /**
     * Type of a given resource context.
     */
    contextType: ResourceContextType;

    /**
     * data factory resourceId ID when contextType is 'mountedDataFactory'.
     */
    dataFactoryResourceId?: string;

    /**
     * Workspace object ID when contextType only is 'mountedDataFactory'.
     */
    workspaceObjectId?: string;
}

export interface IValidationContext {
    contextData: IResourceContext;
    isPublish?: boolean;
    isRetryStage?: boolean;
    validateConnectionPermissionOnly?: boolean;
    changedResources?: IResourceBase[];
    inlineDataset?: boolean;
}

/*
    Annotation on a model class. All modelled classes which represents concepts
    should have an annotation tag
*/
export interface IModelDefinition {
    /*
        Is the class reusable. Meaning it has a identity of its own
        e.g. Pipeline, DataFlow, Dataset, Linked service
        Default: false
    */
    reusable?: boolean;

    /*
        Object of this type can be referenced elsewhere
        Default: false (always contained/owned)
    */
    referable?: boolean;
    /*
        Does the class react to changes. Should implement the Reactor interface
        Default: false;
    */
    reactor?: boolean;
    /*
        Type name for this model. Default is the class name
    */
    name?: string;

    /*
        This is the display name for this model. Singular and Plural.
    */
    display?: string[];

    /*
        If this class is abstract. Default is false
    */
    abstract?: boolean;

    /*
        A instance of this class has no persistence. Default is false
    */
    transient?: boolean;

    /*
        Applies only to referable classes. If this reference is local within a
        concept/document or it is global. Default is false. Any reusable object
        is always global scope
    */
    globalScope?: boolean;

    /*
        Custom tags which can be looked up. Tags in derived classes will be merged with tags
        in the base class and conflicting tags will be overwritten
    */
    readonly tags?: { [tag: string]: {} };

    /*
        Given any attribute you can define/redefine its constraints. Redefinition are useful
        when you have to make a validation stricter(or extend) than what is in the base class.
        For e.g name attribute Named to be more constrained in the derived class
    */
    readonly constraints?: { [attribute: string]: AnyConstraint };

    /*
        All the attribute constraints are first run and then the handler is invoked.
        It is the responsibility of this validator to call the super validator. The framework
        will NOT call this implicitly because there might be override behavior in the derived class
    */
    readonly validator?: ModelValidator;

    /*
        Access control constraint for the given IModel type.
    */
    readonly accessPublishConstraint?: IAccessControlConstraint;

    /*
        If the model is created from unknown type
    */
    readonly createdFromUnknownType?: boolean;

    /**
     * If the model is disabled, the model will be registered as <model.name>_disabled.
     * We still register it because we need the attribute in the model as it might be inherited by other enabled models
     * Please note, the corresponding adapter might not be registered. Ideally, both model and adapter are not actively used.
     */
    readonly disabled?: boolean;
}

/*
    A violation message that can be crafted by using the owner. If the owner
    changes the name the violation is still valid
*/
export interface IViolation {
    /*
        Dynamic message for this violation
    */
    message: () => string;
    messageType?: 'missing' | 'unsupported' | 'invalid' | 'duplicated'

    /*
        message should be localized, while logDetails is in English for debugging purpose
        current format: <ClassName.functionName> - <ErrorType>;
     */
    logDetails?: { // temporary - eventually required field
        functionName?: string; // where is the validation, typical format <ClassName.functionName>
        errorType: ValidationErrorType; // quick categorization of error
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        details?: {[key: string]: any}; // additional details to help with debugging, should compliment message()
    };

    /*
        Context object only applicable for model level validator where
        specific context needs to be filled up. For e.g Pipeline validator
        which checks for cycle and returns a specific activity where there is
        a cycle.
        Multiple context objects may be part of this validation. For unique name
        validation
    */
    context?: {} | Array<{}>;

    /*
        List of all attributes that point to this violation
    */
    attributes?: IAttribute[];

    /*
        If the validation entity belongs to another concept, we can set the redirectTo to redirect the navigation to the specified tab
    */
    redirectTo?: {
        entity?: Named;
        attribute?: IAttribute;
    };

    entity?: Named;

    /*
        if a violation is fetched by some expensive operation, eg. API call, we can set this value as true to specify not automatically refresh whether violation has been corrected.
    */
    doNotAutomaticallyCorrect?: boolean;

    isConnectionPermissionError?: boolean;

    /*
        Method to revalidate this specific violation instance.
        Returns true if corrected. False otherwise.
    */
    revalidate?: () => boolean;
}

export interface IOwnedViolation extends IViolation {
    /*
        Nearest owned object where the violation occured. Concept can be obtained
        by owned.concept
    */
    owned: Owned;

    /*
        Alternative name to display in the violation pane.
    */
    customDisplayName?: string;

    /*
        Disable button click.
    */
    disableClick?: boolean;

    /*
        Attribute reference for the violation
    */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    attribute: any;
    /*
        Attempts to clear the violation provided things are rectified. Like expungement
        after things are fixed. It will return false if it is not cleared. Remember that
        this is for this specific violations. Other violations may have been committed
        during fixing this
    */
    corrected$: Observable<boolean>; // we can't call async function in html, change it to an observable here for html
}

export interface IConstraint {
    /*
        Textual name for this constraint. This will be used in the condition
    */
    name?: string;
    /*
        If this is required unconditionally or it is conditionally required
        if another attribute has a truthy value
    */
    required?: boolean | string[];

    /*
        If this attribute should not be specified if any of the attributes in
        the list of attributes have a truthy value. For e.g Either tableName or
        query should be specified
    */
    optional?: string[];
    /*
        Applies to collection attributes. Minimum items in the list
    */
    minOccurs?: number;
    /*
        Applies to collection attributes. Maximum items in the list
    */
    maxOccurs?: number;

    /*
        Applies to named collections. Unique not case-sensitive
    */
    unique?: boolean;

    /*
        Applies to named collections. Unique case-sensitive
    */
    uniqueCaseSensitive?: boolean;

    /*
        Applies to models. Unique name across all models of same type.
    */
    uniqueModelName?: boolean;

    /*
        If a attribute has to be redefined in a derived class. Applies only to derived model classes.
        For e.g Dataset linked service
    */
    model?: IModel;

    /*
        For any attribute, given an owner(typically Owned) and current value
        whether there were any constraint violations or it passed
    */
    validator?: (
        owner?: {},
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value?: any,
        violation?: IViolation,
        context?: IValidationContext
    ) => IViolation[] | undefined | Promise<IViolation[] | undefined>;
}

export interface IStringConstraintPattern {
    message: string; // human readable message to show on UI
    regex: RegExp; // regex pattern for string attribute
}
export interface IStringConstraint extends IConstraint {
    /*
        Regex pattern for string attribute
    */
    pattern?: RegExp;
    /*
        List of regex patterns for string attribute
    */
    patterns?: IStringConstraintPattern[];
    /*
        Maximum length for this string
    */
    maxLength?: number;

    /*
        List of permissible values. Enumerations and types can be bypassed in typescript
        and should be explictly constrained
    */
    allowed?: string[];
}

export interface INumberConstraint extends IConstraint {
    /*
        Constrained only for whole numbers(not fractions/decimals)
    */
    whole?: boolean;
    /*
        Minimum value for this number. Default is Number.NEGATIVE_INFINITY
    */
    minimum?: number;
    /*
        Maximum value for this number. Default is Number.POSITIVE_INFINITY
    */
    maximum?: number;
}

export interface ITimespanConstraint extends IConstraint {
    /*
        Minimum value for this timespan (as a timespan string). Default is undefined.
    */
    minimum_timespan?: string;
    /*
        Maximum value for this timespan (as a timespan string). Default is undefined.
    */
    maximum_timespan?: string;
}

export interface IAccessControlConstraint {
    /*
        Required scope/action to delete.
    */
    deleteResource: IAccessControlItem;

    /*
        Required scope/action to write.
    */
    writeResource: IAccessControlItem;

    /*
        Required scope/action to read.
    */
    readResource?: IAccessControlItem;
}

export interface IAccessControlItem {
    /*
        List of actions required to publish the resource.
    */
    action: string;
    /*
        Scope of the resource. defaults to workspace/{workspaceName}
    */
    scope?: () => string;
}

/*
    Annotation on a model class. All modelled classes which represents concepts
    should have an annotation tag
*/
export interface IModel {
    /*
        Is the class reusable. Meaning it has a identity of its own
        e.g. Pipeline, DataFlow, Dataset, Linked service
        Default: false
    */
    readonly reusable?: boolean;

    /*
        Object of this type can be referenced elsewhere
        Default: false (always contained/owned)
    */
    readonly referable?: boolean;

    /*
        Scope if global for a referable model
    */
    readonly globalScope?: boolean;

    /*
        Constructor for this model
    */
    readonly construct: Function;

    /*
        Does the class react to changes. Should implement the Reactor interface
        Default: false;
    */
    readonly reactor?: boolean;
    /*
        Type name for this model. Default is the class name
    */
    readonly name: string;

    /*
        Display name for this model. A display name is not defaulted.
        If unavailable it will use its owner's name as the display name
        For e.g Dataset's fileformat may not have a display name
        Has the singular and plural form
    */
    readonly display: string[];

    /*
        If this class is abstract. Default is false
    */
    readonly abstract?: boolean;

    /*
        A instance of this class has no persistence. Default is false
    */
    readonly transient?: boolean;

    /*
        Super model of this class. This is optional
    */
    readonly superModel?: IModel;

    /*
       Returns all the sub models of this class
       <code>
           Metamodel.modelFor(Activity).subModels; returns IModels for DataflowActivity, SparkActivity etc
       </code>
   */
    readonly subModels: IModel[];

    /*
        Returns the super model hierarchy
         <code>
            Metamodel.modelFor(SparkActivity).superModels; returns IModel for Activity
        </code>
    */
    readonly superModels: IModel[];

    /*
        Given a object it will return last super class(or self) which is reusable
        <code>
            Metamodel.modelFor(HDInsightLinkedService).conceptModel ===
                    Metamodel.modelFor(LinkedService);
            Metamodel.modelFor(DataFlow).conceptModel ===
                    Metamodel.modelFor(DataFlow);
        </code>
    */
    readonly concept?: IModel;

    /*
        Given any attribute you can define/redefine its constraints. Redefinition are useful
        when you have to make a validation stricter(or extend) than what is in the base class.
        For e.g name attribute Named to be more constrained in the derived class
    */
    readonly constraints?: { [attribute: string]: AnyConstraint };

    /*
        All attributes for this model including derived attributes
        To find if this attribute is inherited you can go up the model chain and test this collection
    */
    readonly attributes: IAttribute[];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly validator?: ModelValidator;

    readonly accessPublishConstraint?: IAccessControlConstraint;

    readonly createdFromUnknownType?: boolean;

    /*
        Creates an instance of the model given its imodel
        <code>
            subModels = MetaModel.modelFor(Activity).subModels();
            for (const subModel in subModels) {
                const activity = subModel.create<Activity(); //Create an instance of every derived activity

            }
        </code>
    */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    create<T>(...parameters: any[]): T;

    /*
        Create a proxy instance for a model. Model can even be a abstract model
        <code>
            const dataset = Metamodel.modelFor(Dataset).createProxy();
        </code>
    */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    createProxy<T>(...parameters: any[]): T;

    /*
        Checks if this object is an instance of this model
        <code>
            const named:Named = new SparkActivity();
            Metamodel.modelFor(Activity).isInstance(named) === true
        </code>
    */
    isInstance(object: {}): boolean;

    /*
        If this model is a parent/ancestor of the descendantModel
    */
    isAncestor(descendantModel: IModel): boolean;

    /*
         Queries if this attribute is registered with this model
    */
    hasAttribute(name: string): boolean;

    /*
        Returns the attribute definition for this property
    */
    attribute(name: string): IAttribute;

    /*
        Given a key return its value
    */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tag<T>(name: string): T | undefined;

    /*
        Returns a copy of the tags. Changing it does not affect the model
    */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tags(): { [key: string]: any } | undefined;
}

export enum Collection {
    Array,
    Set,
    Map
}

/*
    Annotation on a model attribute.
*/
export interface IAttributeDefinition {
    /*
        Does this attribute represent a reference or is containment.
        Default: false
    */
    reference?: boolean | ((context: { inlineDataset?: boolean }) => boolean);

    /*
        Handler called when the proxy reference is updated by the model service.
    */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    referenceUpdate?: (value: any) => void;

    /*
        Does the class react to changes. Should implement the Reactor interface
        Default: false
    */
    transient?: boolean;

    /*
        Custom tags which can be looked up. You can redefine a tag in the derived class
        with more characteristics
    */
    readonly tags?: { [tag: string]: {} };

    /*
        All constraints on this attributes
    */
    readonly constraint?: IConstraint | IStringConstraint | INumberConstraint | ITimespanConstraint;

    /*
        Display name for this property
    */
    readonly display?: string;

    /*
        Type of collection
    */
    readonly collection?: Collection;

    /*
        Reflect on the type of this property. Either primitive or model
        Applies to collection properties also
    */
    // eslint-disable-next-line @typescript-eslint/array-type
    readonly type?: IModel | (() => IModel);

    /*
        Secondary property used in maps only
    */
    // eslint-disable-next-line @typescript-eslint/array-type
    readonly type2?: IModel | (() => IModel);

    /**
     * Can this attribute be templatable
     */
    readonly templatable?: boolean;

    /**
     * If the attribute is templatable, indicates expression types it can accept.
     */
    readonly accepts?: ExpressionType[];

    /*
        A function that generates the valid expression return types at run-time (as opposed to the
        property 'accepts' which is lists the return types statically).
    */
    readonly dynamicAccepts?: AcceptedTypesRetriever;

    /**
     * Helps associate a static member of that class.
     * If defined that static member is set to this attribute instance
     */
    readonly association?: string;

    /**
     * Skip validation for undefined value in Array.
     */
    readonly ignoreUndefinedInArray?: boolean;

    readonly bypassExpressionValidation?: (entity: Owned) => boolean;

    /**
     * The default value for the attribute, used when we need to normalize the attribute value before sending to backend
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly defaultValue?: any;
}

export type AcceptedTypesRetriever = (entity: Owned) => ExpressionType[];

export type IDatatype = typeof String.name | typeof Number.name | typeof Boolean.name;

/*
    Annotation on a model attribute.
*/
export interface IAttribute {
    /*
        Does this attribute represent a reference or is containment.
        Default: false
    */
    readonly reference?: boolean | ((context: { inlineDataset?: boolean }) => boolean);

    /*
        Handler called when the proxy reference is updated by the model service.
    */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly referenceUpdate?: (value: any) => void;

    /*
        Does the class react to changes. Should implement the Reactor interface
        Default: false
    */
    readonly transient?: boolean;

    /*
        Type name for this attribute. Default is the property name.
    */
    readonly name: string;

    /*
        This is the display name for this model
    */
    readonly display: string;

    /*
        All constraints on this attributes
    */
    readonly constraint?: IConstraint | IStringConstraint | INumberConstraint | ITimespanConstraint;

    /*
        Whether this is a primitive attribute
    */
    readonly primitive?: boolean;

    /*
        Type of collection
    */
    readonly collection?: Collection;

    /**
     * Can this attribute be templatable
     */
    readonly templatable?: boolean;

    /**
     * If the attribute is templatable, indicates expression types it can accept.
     */
    readonly accepts?: ExpressionType[];

    /*
        A function that generates the valid expression return types at run-time (as opposed to the
        property 'accepts' which is lists the return types statically).

    */
    readonly dynamicAccepts?: AcceptedTypesRetriever;

    /**
     * Skip validation for undefined value in Array.
     */
    readonly ignoreUndefinedInArray?: boolean;

    /*
        Who own this model
    */
    readonly owner?: IModel;

    readonly bypassExpressionValidation?: (entity: Owned) => boolean;

    /**
     * The default value for the attribute, used when we need to normalize the attribute value before sending to backend
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly defaultValue?: any;

    /*
        Datatype if it is primitive
    */
    datatype(): IDatatype;

    /*
        ModelType if it is not primitive
    */
    modelType(): IModel;

    /*
        ModelType if it is map
    */
    modelType2(): IModel | undefined;

    /**
     * Gets the value of this attribute on this object
     * Object should be of a model having this attribute
     * attribute.owner.isAncestor(Metamodel.modelOf(object))
     */
    value<T>(object: {}): T | undefined;

    /**
     * Sets the value for object instance for a certain attribute
     */
    setValue<T>(object: {}, value: T | undefined, keepTemplateValue?: boolean): void;

    /**
     * Gets the template value on this value. It is illegal to set
     * the value and templateValue at the same time
     */
    templateValue(object: {}, templateProp?: string): string | undefined;

    /**
     * Sets the template value on this value. It is illegal to set
     * the value and templateValue at the same time
     */
    setTemplateValue(object: {}, templateValue: string | undefined, templateProp?: string): void;

    /**
     * A consolidated interface to tell this attribute represents a reference or is containment.
     */
    isReferenced(context: { inlineDataset?: boolean }): boolean;
}

export interface IRegistry {
    constructors: Function[];
}
